/**
 * @element qds-help-button
 * 
 * A help button component for providing access to documentation and support.
 * 
 * @attr {string} href - URL that the help button links to
 * @attr {string} target - Where to open the link (_blank, _self, etc.)
 * 
 * @csspart button - The button element
 * @csspart icon - The icon element
 * 
 * @fires click - Fired when the button is clicked
 */

import { adoptPrimeIconsStyles, loadBaseCSS, getBaseButtonStyles } from '../shared/button-utils.js';

// Custom event interface
interface HelpButtonClickEventDetail {
  href: string;
}

// Load the base CSS
loadBaseCSS();

export class QdsHelpButton extends HTMLElement {
  // Private properties
  private _href: string;
  private _target: string;
  private _boundClickHandler?: (event: MouseEvent) => void;

  static get observedAttributes(): string[] {
    return ['href', 'target'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize properties with default values
    this._href = '#';
    this._target = '_blank';
    
    this.render();
  }
  
  // Lifecycle callbacks
  connectedCallback(): void {
    if (!this.isConnected) return;
    
    // Set default attributes if not already set
    if (!this.hasAttribute('href')) {
      this.setAttribute('href', this._href);
    }
    if (!this.hasAttribute('target')) {
      this.setAttribute('target', this._target);
    }
    
    // Store bound handler for proper cleanup
    this._boundClickHandler = this._handleClick.bind(this);
    
    // Add click event listener
    // Attach listener on the host element instead of the shadow button
    // so the handler remains active even if shadow DOM is re-rendered.
    this.addEventListener('click', this._boundClickHandler);
  }

  disconnectedCallback(): void {
    // Clean up event listeners
    if (this._boundClickHandler) {
        this.removeEventListener('click', this._boundClickHandler);
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'href':
        this._href = newValue || '#';
        break;
      case 'target':
        this._target = newValue || '_blank';
        break;
    }
    
    this.render();
  }

  // Getters and setters
  get href(): string {
    return this._href;
  }
  
  set href(value: string) {
    this.setAttribute('href', value);
  }
  
  get target(): string {
    return this._target;
  }
  
  set target(value: string) {
    this.setAttribute('target', value);
  }

  // Event handlers
  private _handleClick(event: MouseEvent): void {
    if (this._href && this._href !== '#') {
      window.open(this._href, this._target);
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent<HelpButtonClickEventDetail>('helpClick', {
      bubbles: true,
      composed: true,
      detail: { href: this._href }
    }));
  }

  // Render the component
  private render(): void {
    if (!this.shadowRoot) return;

    const styles = `
      ${getBaseButtonStyles()}

      .menu-label {
        display: none;
        margin-left: 8px;
        font-size: 14px;
        color: var(--theme-text-secondary, var(--theme-text-primary, inherit));
      }

      /* Show label in mobile menu */
      @media (max-width: 480px) {
        .mobile-menu .menu-label {
          display: inline-block;
        }
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <button part="button" aria-label="Help">
        <i class="pi pi-question-circle icon" part="icon"></i>
        <span class="menu-label">Help</span>
      </button>
    `;
    void adoptPrimeIconsStyles(this.shadowRoot);
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-help-button')) {
  customElements.define('qds-help-button', QdsHelpButton);
}
